/**
 * Formats 24-hour time to 12-hour format with AM/PM
 * @param {string} time24 - Time in 24-hour format (e.g., "14:30")
 * @returns {string} Time in 12-hour format with AM/PM (e.g., "2:30 PM")
 */
export const formatTime = (time24) => {
  if (!time24) {
    return '';
  }

  const [hour24, minute] = time24.split(':');
  const hourNum = parseInt(hour24, 10);
  let hour12 = hourNum;

  if (hourNum === 0) {
    hour12 = 12;
  } else if (hourNum > 12) {
    hour12 = hourNum - 12;
  }

  const ampm = hourNum >= 12 ? 'PM' : 'AM';

  return `${hour12}:${minute} ${ampm}`;
};

/**
 * Converts 12-hour format to 24-hour format
 * @param {string} hour12 - Hour in 12-hour format (1-12)
 * @param {string} minute - Minutes (00-59)
 * @param {string} ampm - "AM" or "PM"
 * @returns {string} Time in 24-hour format (e.g., "14:30")
 */
export const convertTo24Hour = (hour12, minute, ampm) => {
  let hour24 = parseInt(hour12, 10);

  // NOTE(vlad|SPD-14091:2026-02-05): Handle empty or invalid hour
  if (isNaN(hour24)) {
    hour24 = ampm === 'AM' ? 0 : 12;
  }

  if (ampm === 'AM' && hour24 === 12) {
    hour24 = 0;
  } else if (ampm === 'PM' && hour24 !== 12) {
    hour24 += 12;
  }

  return `${String(hour24).padStart(2, '0')}:${minute || '00'}`;
};

/**
 * Parses 24-hour time into components
 * @param {string} time24 - Time in 24-hour format
 * @returns {{hour12: string, minute: string, ampm: string}}
 */
export const parseTime = (time24) => {
  if (!time24) {
    return {
      hour12: '12',
      minute: '00',
      ampm: 'AM',
    };
  }

  const [hour24Str, minute] = time24.split(':');
  const hour24 = parseInt(hour24Str, 10);
  let hour12 = hour24;

  if (hour24 === 0) {
    hour12 = 12;
  } else if (hour24 > 12) {
    hour12 = hour24 - 12;
  }

  const ampm = hour24 >= 12 ? 'PM' : 'AM';

  return {
    hour12: String(hour12),
    minute: minute || '00',
    ampm,
  };
};

/**
 * Validates hour input (1-12 for 12-hour format)
 * @param {string} value - Hour value to validate
 * @returns {boolean} True if valid
 */
export const isValidHour = (value) => {
  const num = parseInt(value, 10);

  return !isNaN(num) && num >= 1 && num <= 12;
};

/**
 * Validates minute input (0-59)
 * @param {string} value - Minute value to validate
 * @returns {boolean} True if valid
 */
export const isValidMinute = (value) => {
  const num = parseInt(value, 10);

  return !isNaN(num) && num >= 0 && num <= 59;
};

/**
 * Pads a number with leading zeros
 * @param {string|number} value - Value to pad
 * @param {number} length - Desired length
 * @returns {string} Padded value
 */
export const padNumber = (value, length = 2) => {
  return String(value).padStart(length, '0');
};

/**
 * Checks if time is set to default range (00:00 - 23:59)
 * @param {string} startTime - Start time in 24-hour format
 * @param {string} endTime - End time in 24-hour format
 * @returns {boolean} True if times are at defaults
 */
export const isDefaultTimeRange = (startTime, endTime) => {
  return startTime === '00:00' && endTime === '23:59';
};

/**
 * Formats time for display (removes leading zeros from hours)
 * @param {string} time24 - Time in 24-hour format
 * @returns {string} Formatted time string (e.g., "2:30 PM")
 */
export const formatTimeDisplay = (time24) => {
  if (!time24) {
    return '';
  }

  const { hour12, minute, ampm } = parseTime(time24);

  return `${parseInt(hour12, 10)}:${minute} ${ampm}`;
};

/**
 * Formats a time range for display
 * @param {string} startTime - Start time in 24-hour format
 * @param {string} endTime - End time in 24-hour format
 * @returns {string} Formatted time range (e.g., "9:30 AM - 5:00 PM")
 */
export const formatTimeRangeDisplay = (startTime, endTime) => {
  if (!startTime || !endTime) {
    return '';
  }

  return `${formatTimeDisplay(startTime)} - ${formatTimeDisplay(endTime)}`;
};
